import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Vote, Trophy, TrendingUp, Building, MapPin, Search,
  ExternalLink, Phone, Mail, Globe, Award, Download, File, FileSpreadsheet, Printer, Ban
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Party-specific colors
const PARTY_COLORS = {
  'BJP': '#FF9933',
  'INC': '#19AAED',
  'NCP': '#00BFFF',
  'SS': '#FF6600',
  'SS-UBT': '#F97316',
  'BSP': '#22C55E',
  'AIMIM': '#22C55E',
  'IUML': '#138808',
};

const DEFAULT_COLORS = ['#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F59E0B'];

const getPartyColor = (partyName, index) => {
  return PARTY_COLORS[partyName] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
};

const CitizenPortal = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [zoneSummary, setZoneSummary] = useState([]);
  const [pollingData, setPollingData] = useState([]);
  const [partywiseVoting, setPartywiseVoting] = useState([]);
  const [leadershipData, setLeadershipData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedZone, setSelectedZone] = useState('all');
  const [language, setLanguage] = useState('en');
  const [showCelebration, setShowCelebration] = useState(false);
  const confettiTriggered = useRef(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Confetti celebration effect for elected leaders
  useEffect(() => {
    const electedLeaders = leadershipData.filter(
      l => (l.position === 'Mayor' || l.position === 'Deputy Mayor') && l.status === 'Elected'
    );
    
    if (electedLeaders.length > 0 && !confettiTriggered.current) {
      confettiTriggered.current = true;
      setShowCelebration(true);
      
      // Start confetti rain for 8 seconds
      const duration = 8 * 1000;
      const animationEnd = Date.now() + duration;
      const colors = ['#FF9933', '#19AAED', '#FFD700', '#00FF00', '#FF69B4'];

      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });

        if (Date.now() < animationEnd) {
          requestAnimationFrame(frame);
        }
      };
      
      frame();
      
      // Stop celebration after 8 seconds
      setTimeout(() => {
        setShowCelebration(false);
      }, duration);
    }
  }, [leadershipData]);

  const fetchData = async () => {
    try {
      const [statsRes, candidatesRes, zoneRes, pollingRes, partywiseRes, leadershipRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/elected-candidates`),
        axios.get(`${API}/zone-summary`),
        axios.get(`${API}/polling-percentage`),
        axios.get(`${API}/partywise-voting`),
        axios.get(`${API}/leadership-selection`)
      ]);
      setStats(statsRes.data);
      setCandidates(candidatesRes.data);
      setZoneSummary(zoneRes.data);
      setPollingData(pollingRes.data);
      setPartywiseVoting(partywiseRes.data);
      setLeadershipData(leadershipRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Download as PDF with proper Marathi support
  const downloadPDF = async () => {
    // Create a hidden div with the report content
    const reportDiv = document.createElement('div');
    reportDiv.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 800px; padding: 20px; font-family: Arial, "Noto Sans Devanagari", sans-serif; background: white;';
    
    reportDiv.innerHTML = `
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="color: #003366; margin: 0;">Nagpur Municipal Corporation</h1>
        <h2 style="color: #003366; margin: 5px 0;">नागपूर महानगरपालिका</h2>
        <h3 style="color: #666;">General Election Results 2025-26</h3>
        <h3 style="color: #666;">सार्वत्रिक निवडणूक निकाल 2025-26</h3>
        <p style="color: #999;">Generated: ${new Date().toLocaleDateString()}</p>
      </div>
      
      <div style="margin-bottom: 30px;">
        <h3 style="color: #003366; border-bottom: 2px solid #003366; padding-bottom: 5px;">Election Summary / निवडणूक सारांश</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Voters / एकूण मतदार</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${stats?.total_voters?.toLocaleString() || 0}</td></tr>
          <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>Total Seats / एकूण जागा</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${stats?.total_seats || 0}</td></tr>
          <tr><td style="padding: 8px; border: 1px solid #ddd;"><strong>Average Polling / सरासरी मतदान</strong></td><td style="padding: 8px; border: 1px solid #ddd;">${stats?.avg_polling_percentage || 0}%</td></tr>
        </table>
      </div>
      
      <div style="margin-bottom: 30px;">
        <h3 style="color: #003366; border-bottom: 2px solid #003366; padding-bottom: 5px;">Party-wise Seats Won / पक्षनिहाय जिंकलेल्या जागा</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="background: #003366; color: white;">
            <th style="padding: 10px; border: 1px solid #ddd;">Party / पक्ष</th>
            <th style="padding: 10px; border: 1px solid #ddd;">Seats / जागा</th>
          </tr>
          ${stats?.party_seats ? Object.entries(stats.party_seats)
            .sort((a, b) => b[1] - a[1])
            .map(([party, seats]) => `<tr><td style="padding: 8px; border: 1px solid #ddd;">${party}</td><td style="padding: 8px; border: 1px solid #ddd; text-align: center;"><strong>${seats}</strong></td></tr>`)
            .join('') : ''}
        </table>
      </div>
      
      <div style="margin-bottom: 30px;">
        <h3 style="color: #003366; border-bottom: 2px solid #003366; padding-bottom: 5px;">Zone-wise Summary / झोननिहाय सारांश</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <tr style="background: #003366; color: white;">
            <th style="padding: 8px; border: 1px solid #ddd;">Zone / झोन</th>
            <th style="padding: 8px; border: 1px solid #ddd;">Seats / जागा</th>
            <th style="padding: 8px; border: 1px solid #ddd;">Voters / मतदार</th>
            <th style="padding: 8px; border: 1px solid #ddd;">Polling / मतदान</th>
            <th style="padding: 8px; border: 1px solid #ddd;">Leading / अग्रणी</th>
          </tr>
          ${zoneSummary.map(zone => {
            const leading = Object.keys(zone.party_distribution).sort((a, b) => zone.party_distribution[b] - zone.party_distribution[a])[0] || '-';
            return `<tr>
              <td style="padding: 6px; border: 1px solid #ddd;">${zone.zone}</td>
              <td style="padding: 6px; border: 1px solid #ddd; text-align: center;">${zone.total_seats}</td>
              <td style="padding: 6px; border: 1px solid #ddd; text-align: right;">${zone.total_voters.toLocaleString()}</td>
              <td style="padding: 6px; border: 1px solid #ddd; text-align: center;">${zone.avg_polling_percentage}%</td>
              <td style="padding: 6px; border: 1px solid #ddd; text-align: center;"><strong>${leading}</strong></td>
            </tr>`;
          }).join('')}
        </table>
      </div>
      
      <div>
        <h3 style="color: #003366; border-bottom: 2px solid #003366; padding-bottom: 5px;">All Elected Candidates / सर्व निर्वाचित उमेदवार (${candidates.length})</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
          <tr style="background: #003366; color: white;">
            <th style="padding: 5px; border: 1px solid #ddd;">Zone/झोन</th>
            <th style="padding: 5px; border: 1px solid #ddd;">Ward/प्रभाग</th>
            <th style="padding: 5px; border: 1px solid #ddd;">Winner/विजेता</th>
            <th style="padding: 5px; border: 1px solid #ddd;">Party/पक्ष</th>
            <th style="padding: 5px; border: 1px solid #ddd;">Votes/मते</th>
            <th style="padding: 5px; border: 1px solid #ddd;">Margin/मताधिक्य</th>
          </tr>
          ${candidates.map(c => `<tr>
            <td style="padding: 4px; border: 1px solid #ddd;">${c.zone || '-'}</td>
            <td style="padding: 4px; border: 1px solid #ddd;">${c.ward_no}-${c.seat_no}</td>
            <td style="padding: 4px; border: 1px solid #ddd;">${c.winner_name}</td>
            <td style="padding: 4px; border: 1px solid #ddd;">${c.winner_party}</td>
            <td style="padding: 4px; border: 1px solid #ddd; text-align: right;">${c.winner_votes.toLocaleString()}</td>
            <td style="padding: 4px; border: 1px solid #ddd; text-align: right;"><strong>${c.margin.toLocaleString()}</strong></td>
          </tr>`).join('')}
        </table>
      </div>
    `;
    
    document.body.appendChild(reportDiv);
    
    try {
      const canvas = await html2canvas(reportDiv, { 
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save('NMC_Election_Results_2025-26.pdf');
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      document.body.removeChild(reportDiv);
    }
  };

  // Download as Excel
  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    const summaryData = [
      ['Nagpur Municipal Corporation - Election Results 2025-26'], 
      ['नागपूर महानगरपालिका - निवडणूक निकाल 2025-26'],
      [''],
      ['Summary Statistics / सारांश आकडेवारी'],
      ['Total Voters / एकूण मतदार', stats?.total_voters || 0],
      ['Total Seats / एकूण जागा', stats?.total_seats || 0],
      ['Average Polling % / सरासरी मतदान %', stats?.avg_polling_percentage || 0], 
      [''],
      ['Party-wise Seats Won / पक्षनिहाय जिंकलेल्या जागा'], 
      ['Party / पक्ष', 'Seats / जागा'],
      ...(stats?.party_seats ? Object.entries(stats.party_seats).sort((a, b) => b[1] - a[1]) : [])
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');
    
    const zoneHeaders = ['Zone / झोन', 'Total Seats / एकूण जागा', 'Total Voters / एकूण मतदार', 'Polling % / मतदान %', 'Leading Party / अग्रणी पक्ष', 'BJP', 'INC', 'Others / इतर'];
    const zoneRows = zoneSummary.map(zone => [
      zone.zone, zone.total_seats, zone.total_voters, zone.avg_polling_percentage,
      Object.keys(zone.party_distribution).sort((a, b) => zone.party_distribution[b] - zone.party_distribution[a])[0] || '-',
      zone.party_distribution['BJP'] || 0, zone.party_distribution['INC'] || 0,
      Object.entries(zone.party_distribution).filter(([p]) => p !== 'BJP' && p !== 'INC').reduce((sum, [, count]) => sum + count, 0)
    ]);
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([zoneHeaders, ...zoneRows]), 'Zone Summary');
    
    const candidateHeaders = ['Sr.No / क्र.', 'Zone / झोन', 'Ward / प्रभाग', 'Seat / जागा', 'Reservation / आरक्षण', 'Winner Name / विजेता नाव', 'Winner Party / विजेता पक्ष', 'Winner Votes / विजेता मते', 'Runner-up Name / उपविजेता नाव', 'Runner-up Party / उपविजेता पक्ष', 'Runner-up Votes / उपविजेता मते', 'Margin / मताधिक्य'];
    const candidateRows = candidates.map((c, idx) => [
      idx + 1, c.zone || '-', c.ward_no, c.seat_no, c.reservation, c.winner_name, c.winner_party, c.winner_votes,
      c.runner_up_name, c.runner_up_party, c.runner_up_votes, c.margin
    ]);
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([candidateHeaders, ...candidateRows]), 'Elected Candidates');
    
    const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], 
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'NMC_Election_Results_2025-26.xlsx');
  };

  // Download CSV with UTF-8 BOM for proper Marathi display
  const downloadCSV = () => {
    // Add UTF-8 BOM for Excel compatibility with Marathi
    const BOM = '\uFEFF';
    let csvContent = BOM + 'Sr.No / क्र.,Zone / झोन,Ward / प्रभाग,Seat / जागा,Reservation / आरक्षण,Winner Name / विजेता नाव,Winner Party / विजेता पक्ष,Winner Votes / विजेता मते,Runner-up Name / उपविजेता नाव,Runner-up Party / उपविजेता पक्ष,Runner-up Votes / उपविजेता मते,Margin / मताधिक्य\n';
    candidates.forEach((c, idx) => {
      csvContent += `${idx + 1},"${c.zone || '-'}","${c.ward_no}","${c.seat_no}","${c.reservation}","${c.winner_name}","${c.winner_party}",${c.winner_votes},"${c.runner_up_name}","${c.runner_up_party}",${c.runner_up_votes},${c.margin}\n`;
    });
    saveAs(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }), 'NMC_Elected_Candidates.csv');
  };

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = searchTerm === '' || 
      c.winner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.ward_no?.toString().includes(searchTerm) ||
      c.winner_party?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesZone = selectedZone === 'all' || c.zone === selectedZone;
    return matchesSearch && matchesZone;
  });

  const partyChartData = stats?.party_seats ? 
    Object.entries(stats.party_seats)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value) 
    : [];

  const t = (en, mr) => language === 'en' ? en : mr;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-xl">{t('Loading...', 'लोड होत आहे...')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#003366] to-[#004d99] text-white py-6 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <Building className="h-12 w-12" />
              <div className="text-center md:text-left">
                <h1 className="text-2xl md:text-3xl font-bold">
                  {t('Nagpur Municipal Corporation', 'नागपूर महानगरपालिका')}
                </h1>
                <p className="text-blue-200">
                  {t('General Election Results 2025-26', 'सार्वत्रिक निवडणूक निकाल 2025-26')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant={language === 'en' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setLanguage('en')}
                className="text-white border-white/30"
              >
                English
              </Button>
              <Button 
                variant={language === 'mr' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setLanguage('mr')}
                className="text-white border-white/30"
              >
                मराठी
              </Button>
              <Link to="/login">
                <Button variant="outline" size="sm" className="text-white border-white/50 hover:bg-white/20">
                  {t('Admin Login', 'प्रशासक लॉगिन')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Download Report Section */}
        <Card className="bg-white shadow-md border-2 border-primary/20">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <Download className="h-6 w-6 text-primary" />
                <div>
                  <h3 className="font-bold">{t('Download Election Report', 'निवडणूक अहवाल डाउनलोड करा')}</h3>
                  <p className="text-sm text-muted-foreground">{t('Get complete election results', 'संपूर्ण निवडणूक निकाल मिळवा')}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={downloadPDF} className="flex items-center gap-2" data-testid="citizen-download-pdf">
                  <File className="h-4 w-4" />
                  {t('Download PDF', 'PDF डाउनलोड')}
                </Button>
                <Button onClick={downloadExcel} variant="outline" className="flex items-center gap-2" data-testid="citizen-download-excel">
                  <FileSpreadsheet className="h-4 w-4" />
                  {t('Download Excel', 'Excel डाउनलोड')}
                </Button>
                <Button onClick={downloadCSV} variant="outline" className="flex items-center gap-2" data-testid="citizen-download-csv">
                  <Download className="h-4 w-4" />
                  {t('Download CSV', 'CSV डाउनलोड')}
                </Button>
                <Button onClick={() => window.print()} variant="outline" className="flex items-center gap-2">
                  <Printer className="h-4 w-4" />
                  {t('Print', 'प्रिंट')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('Total Voters', 'एकूण मतदार')}</p>
                  <p className="text-2xl font-bold">{stats?.total_voters?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-full">
                  <Vote className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('Total Seats', 'एकूण जागा')}</p>
                  <p className="text-2xl font-bold">{stats?.total_seats}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-full">
                  <MapPin className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('Polling Stations', 'मतदान केंद्रे')}</p>
                  <p className="text-2xl font-bold">{stats?.total_polling_stations?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-full">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('Avg Polling', 'सरासरी मतदान')}</p>
                  <p className="text-2xl font-bold">{stats?.avg_polling_percentage}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-full">
                  <Ban className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('NOTA Votes', 'नोटा मते')}</p>
                  <p className="text-2xl font-bold">{stats?.total_nota_votes?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">({stats?.nota_percentage}%)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leadership Selection Section - Only Elected Mayor and Deputy Mayor */}
        {leadershipData.filter(l => (l.position === 'Mayor' || l.position === 'Deputy Mayor') && l.status === 'Elected').length > 0 && (
          <Card className="bg-gradient-to-r from-[#003366] to-[#004d99] text-white shadow-xl relative overflow-hidden">
            {/* Congratulations Banner */}
            {showCelebration && (
              <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white py-2 text-center animate-pulse z-10">
                <p className="text-lg font-bold">
                  🎉 {t('Congratulations!', 'अभिनंदन!')} 🎉
                </p>
              </div>
            )}
            <CardHeader className={showCelebration ? 'pt-12' : ''}>
              <CardTitle className="flex items-center gap-2 text-white text-2xl">
                <Award className="h-6 w-6 text-yellow-400" />
                {t('Leadership', 'नेतृत्व')}
              </CardTitle>
              <p className="text-blue-200">
                {t('Mayor & Deputy Mayor', 'महापौर आणि उपमहापौर')}
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {leadershipData
                  .filter(l => (l.position === 'Mayor' || l.position === 'Deputy Mayor') && l.status === 'Elected')
                  .map((leader, idx) => (
                  <div key={leader.id || idx} className="bg-white/10 rounded-lg p-6 backdrop-blur text-center">
                    {/* Photo */}
                    {leader.photo_url ? (
                      <img 
                        src={leader.photo_url} 
                        alt={leader.candidate_name}
                        className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-yellow-400"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-white/20 flex items-center justify-center border-4 border-yellow-400">
                        <Users className="h-12 w-12 text-white/60" />
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-2 mb-2">
                      {leader.position === 'Mayor' && <Award className="h-6 w-6 text-yellow-400" />}
                      {leader.position === 'Deputy Mayor' && <Award className="h-6 w-6 text-blue-300" />}
                      <span className="font-semibold text-yellow-300 text-lg">{leader.position}</span>
                    </div>
                    <p className="text-2xl font-bold">
                      {language === 'mr' && leader.candidate_name_marathi 
                        ? leader.candidate_name_marathi 
                        : leader.candidate_name}
                    </p>
                    {language === 'en' && leader.candidate_name_marathi && (
                      <p className="text-sm text-blue-200 marathi-text">{leader.candidate_name_marathi}</p>
                    )}
                    {language === 'mr' && leader.candidate_name && (
                      <p className="text-sm text-blue-200">{leader.candidate_name}</p>
                    )}
                    <Badge 
                      className="mt-2 text-white text-sm px-4 py-1" 
                      style={{ 
                        backgroundColor: leader.party_name === 'BJP' ? '#FF9933' : 
                                        leader.party_name === 'Congress' ? '#19AAED' : '#808080' 
                      }}
                    >
                      {leader.party_name}
                    </Badge>
                    <div className="mt-4 text-sm text-blue-100 space-y-1">
                      <p>{t('Total Votes', 'एकूण मते')}: <strong className="text-white">{leader.total_votes?.toLocaleString()}</strong></p>
                      <p>{t('Against Votes', 'विरोधी मते')}: <strong className="text-red-300">{leader.win_by_votes?.toLocaleString()}</strong></p>
                      <p>{t('Tatasth Votes', 'तटस्थ मते')}: <strong className="text-orange-300">{leader.tatasth_votes_needed?.toLocaleString()}</strong></p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Winner Announcement */}
        <Card className="bg-gradient-to-r from-[#FF9933] to-[#FF6600] text-white shadow-xl">
          <CardContent className="py-8">
            <div className="text-center">
              <Award className="h-16 w-16 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-2">
                {t('BJP Wins Majority', 'भाजप बहुमत विजयी')}
              </h2>
              <p className="text-xl">
                {t(`${partyChartData[0]?.value || 0} out of ${stats?.total_seats || 0} seats`, 
                   `${stats?.total_seats || 0} पैकी ${partyChartData[0]?.value || 0} जागा`)}
              </p>
              <div className="flex justify-center gap-4 mt-4">
                {partyChartData.slice(0, 3).map((party, idx) => (
                  <Badge key={party.name} className="text-lg px-4 py-2" style={{ backgroundColor: getPartyColor(party.name, idx) }}>
                    {party.name}: {party.value}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                {t('Party-wise Seats Won', 'पक्षनिहाय जिंकलेल्या जागा')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={partyChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {partyChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getPartyColor(entry.name, index)} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-500" />
                {t('Zone-wise Results', 'झोननिहाय निकाल')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={zoneSummary.map(z => ({
                  name: z.zone.replace('Zone ', 'Z'),
                  BJP: z.party_distribution['BJP'] || 0,
                  INC: z.party_distribution['INC'] || 0,
                  Others: Object.entries(z.party_distribution)
                    .filter(([p]) => p !== 'BJP' && p !== 'INC')
                    .reduce((sum, [, v]) => sum + v, 0)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="BJP" fill="#FF9933" name="BJP" />
                  <Bar dataKey="INC" fill="#19AAED" name="INC" />
                  <Bar dataKey="Others" fill="#6366F1" name={t('Others', 'इतर')} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Search Your Ward */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              {t('Find Your Ward Representative', 'आपला प्रभाग प्रतिनिधी शोधा')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                  placeholder={t('Search by name, ward number, or party...', 'नाव, प्रभाग क्रमांक किंवा पक्षाने शोधा...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              <Select value={selectedZone} onValueChange={setSelectedZone}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder={t('Select Zone', 'झोन निवडा')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('All Zones', 'सर्व झोन')}</SelectItem>
                  {zoneSummary.map(zone => (
                    <SelectItem key={zone.zone} value={zone.zone}>{zone.zone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-muted-foreground mb-4">
              {t(`Showing ${filteredCandidates.length} of ${candidates.length} results`, 
                 `${candidates.length} पैकी ${filteredCandidates.length} निकाल दाखवत आहे`)}
            </div>

            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead>{t('Zone', 'झोन')}</TableHead>
                    <TableHead>{t('Ward', 'प्रभाग')}</TableHead>
                    <TableHead>{t('Category', 'प्रवर्ग')}</TableHead>
                    <TableHead>{t('Elected Representative', 'निर्वाचित प्रतिनिधी')}</TableHead>
                    <TableHead>{t('Party', 'पक्ष')}</TableHead>
                    <TableHead>{t('Votes', 'मते')}</TableHead>
                    <TableHead>{t('Margin', 'मताधिक्य')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCandidates.map((candidate, idx) => (
                    <TableRow key={candidate.id || idx}>
                      <TableCell>{candidate.zone || '-'}</TableCell>
                      <TableCell className="font-medium">{candidate.ward_no}-{candidate.seat_no}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{candidate.reservation}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{candidate.winner_name}</div>
                        <div className="text-xs text-muted-foreground">{candidate.winner_gender}</div>
                      </TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: getPartyColor(candidate.winner_party, 0) }} className="text-white">
                          {candidate.winner_party}
                        </Badge>
                      </TableCell>
                      <TableCell>{candidate.winner_votes?.toLocaleString()}</TableCell>
                      <TableCell className="font-bold text-green-600">{candidate.margin?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Zone Summary Cards */}
        <div>
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-primary" />
            {t('Zone-wise Summary', 'झोननिहाय सारांश')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {zoneSummary.map(zone => {
              const leadingParty = Object.keys(zone.party_distribution).sort((a, b) => 
                zone.party_distribution[b] - zone.party_distribution[a]
              )[0];
              return (
                <Card key={zone.zone} className="shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-4">
                    <h3 className="font-bold text-lg mb-2">{zone.zone}</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('Seats', 'जागा')}</span>
                        <span className="font-bold">{zone.total_seats}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('Polling', 'मतदान')}</span>
                        <span className="font-bold">{zone.avg_polling_percentage}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">{t('Winner', 'विजेता')}</span>
                        <Badge style={{ backgroundColor: getPartyColor(leadingParty, 0) }} className="text-white text-xs">
                          {leadingParty}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Zone-wise Results Table */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              {t('Zone-wise Election Results', 'झोननिहाय निवडणूक निकाल')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('Summary of election results by zone', 'झोननुसार निवडणूक निकालांचा सारांश')}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#003366]">
                    <TableHead className="text-white">{t('Zone', 'झोन')}</TableHead>
                    <TableHead className="text-white">{t('Total Seats', 'एकूण जागा')}</TableHead>
                    <TableHead className="text-white">{t('Total Voters', 'एकूण मतदार')}</TableHead>
                    <TableHead className="text-white">{t('Polling %', 'मतदान %')}</TableHead>
                    <TableHead className="text-white">{t('Leading Party', 'अग्रणी पक्ष')}</TableHead>
                    <TableHead className="text-white">BJP</TableHead>
                    <TableHead className="text-white">INC</TableHead>
                    <TableHead className="text-white">{t('Others', 'इतर')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {zoneSummary.map((zone) => {
                    const leadingParty = Object.keys(zone.party_distribution).sort((a, b) => 
                      zone.party_distribution[b] - zone.party_distribution[a]
                    )[0] || '-';
                    const others = Object.entries(zone.party_distribution)
                      .filter(([p]) => p !== 'BJP' && p !== 'INC')
                      .reduce((sum, [, count]) => sum + count, 0);
                    return (
                      <TableRow key={zone.zone} className="hover:bg-muted/50">
                        <TableCell className="font-medium">{zone.zone}</TableCell>
                        <TableCell className="text-center">{zone.total_seats}</TableCell>
                        <TableCell className="text-right">{zone.total_voters.toLocaleString()}</TableCell>
                        <TableCell className="text-center font-semibold">{zone.avg_polling_percentage}%</TableCell>
                        <TableCell>
                          <Badge style={{ backgroundColor: getPartyColor(leadingParty, 0) }} className="text-white">
                            {leadingParty}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge style={{ backgroundColor: '#FF9933' }} className="text-white">
                            {zone.party_distribution['BJP'] || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge style={{ backgroundColor: '#19AAED' }} className="text-white">
                            {zone.party_distribution['INC'] || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">{others}</TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total Row */}
                  <TableRow className="bg-muted font-bold">
                    <TableCell>{t('Total', 'एकूण')}</TableCell>
                    <TableCell className="text-center">{zoneSummary.reduce((sum, z) => sum + z.total_seats, 0)}</TableCell>
                    <TableCell className="text-right">{zoneSummary.reduce((sum, z) => sum + z.total_voters, 0).toLocaleString()}</TableCell>
                    <TableCell className="text-center">
                      {(zoneSummary.reduce((sum, z) => sum + z.avg_polling_percentage, 0) / zoneSummary.length).toFixed(2)}%
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell className="text-center">
                      <Badge style={{ backgroundColor: '#FF9933' }} className="text-white">
                        {zoneSummary.reduce((sum, z) => sum + (z.party_distribution['BJP'] || 0), 0)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge style={{ backgroundColor: '#19AAED' }} className="text-white">
                        {zoneSummary.reduce((sum, z) => sum + (z.party_distribution['INC'] || 0), 0)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {zoneSummary.reduce((sum, z) => {
                        const others = Object.entries(z.party_distribution)
                          .filter(([p]) => p !== 'BJP' && p !== 'INC')
                          .reduce((s, [, c]) => s + c, 0);
                        return sum + others;
                      }, 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Polling Percentage Table */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Vote className="h-5 w-5 text-primary" />
              {t('Polling Percentage', 'मतदान टक्केवारी')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('Zone-wise polling percentage at different time slots', 'विविध वेळेच्या स्लॉटवर झोननिहाय मतदान टक्केवारी')}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="bg-[#003366]">
                    <TableHead className="text-white">{t('Sr.No', 'क्र.')}</TableHead>
                    <TableHead className="text-white">{t('Zone', 'झोन')}</TableHead>
                    <TableHead className="text-white">{t('Prabhag', 'प्रभाग')}</TableHead>
                    <TableHead className="text-white">{t('Total Voters', 'एकूण मतदार')}</TableHead>
                    <TableHead className="text-white">9:30 AM</TableHead>
                    <TableHead className="text-white">11:30 AM</TableHead>
                    <TableHead className="text-white">1:30 PM</TableHead>
                    <TableHead className="text-white">3:30 PM</TableHead>
                    <TableHead className="text-white">5:30 PM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pollingData.map((poll, idx) => (
                    <TableRow key={poll.id || idx} className="hover:bg-muted/50">
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="font-medium">{poll.zone || poll.mc_name}</TableCell>
                      <TableCell>{poll.prabhag_no}</TableCell>
                      <TableCell className="text-right">{poll.total_voters?.toLocaleString()}</TableCell>
                      <TableCell className="text-center">{poll.slot_9_30_percent?.toFixed(2) || 0}%</TableCell>
                      <TableCell className="text-center">{poll.slot_11_30_percent?.toFixed(2) || 0}%</TableCell>
                      <TableCell className="text-center">{poll.slot_1_30_percent?.toFixed(2) || 0}%</TableCell>
                      <TableCell className="text-center">{poll.slot_3_30_percent?.toFixed(2) || 0}%</TableCell>
                      <TableCell className="text-center font-semibold text-green-600">{poll.slot_5_30_percent?.toFixed(2) || 0}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {t(`Showing ${pollingData.length} polling records`, `${pollingData.length} मतदान रेकॉर्ड दाखवत आहे`)}
            </div>
          </CardContent>
        </Card>

        {/* Party-wise Voting Table */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              {t('Party-wise Voting (Statement 8)', 'पक्षनिहाय मतदान (विवरण 8)')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('Vote distribution by party across all wards', 'सर्व प्रभागांमध्ये पक्षनिहाय मतांचे वितरण')}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="bg-[#003366]">
                    <TableHead className="text-white">{t('Sr.', 'क्र.')}</TableHead>
                    <TableHead className="text-white">{t('Zone', 'झोन')}</TableHead>
                    <TableHead className="text-white">{t('Prabhag', 'प्रभाग')}</TableHead>
                    <TableHead className="text-white">{t('Reservation', 'आरक्षण')}</TableHead>
                    <TableHead className="text-white text-right" style={{ color: '#FF9933' }}>BJP</TableHead>
                    <TableHead className="text-white text-right" style={{ color: '#19AAED' }}>INC</TableHead>
                    <TableHead className="text-white text-right">NCP</TableHead>
                    <TableHead className="text-white text-right">BSP</TableHead>
                    <TableHead className="text-white text-right">SS-UBT</TableHead>
                    <TableHead className="text-white text-right">MNS</TableHead>
                    <TableHead className="text-white text-right">IND</TableHead>
                    <TableHead className="text-white text-right text-red-300">NOTA</TableHead>
                    <TableHead className="text-white text-right">{t('Total', 'एकूण')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partywiseVoting.map((row, idx) => (
                    <TableRow key={row.id || idx} className="hover:bg-muted/50">
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="font-medium">{row.zone}</TableCell>
                      <TableCell>{row.prabhag_no}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {row.reservation || row.seat_no}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium" style={{ color: '#FF9933' }}>
                        {row.bjp?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell className="text-right font-medium" style={{ color: '#19AAED' }}>
                        {row.inc?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell className="text-right">{row.ncp?.toLocaleString() || 0}</TableCell>
                      <TableCell className="text-right">{row.bsp?.toLocaleString() || 0}</TableCell>
                      <TableCell className="text-right">{row.ss_ubt?.toLocaleString() || 0}</TableCell>
                      <TableCell className="text-right">{row.mns?.toLocaleString() || 0}</TableCell>
                      <TableCell className="text-right">{row.independent?.toLocaleString() || 0}</TableCell>
                      <TableCell className="text-right text-red-500 font-medium">
                        {row.nota?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {row.total_votes?.toLocaleString() || 0}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow className="bg-muted font-bold sticky bottom-0">
                    <TableCell colSpan={4}>{t('Total', 'एकूण')}</TableCell>
                    <TableCell className="text-right" style={{ color: '#FF9933' }}>
                      {partywiseVoting.reduce((sum, r) => sum + (r.bjp || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#19AAED' }}>
                      {partywiseVoting.reduce((sum, r) => sum + (r.inc || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {partywiseVoting.reduce((sum, r) => sum + (r.ncp || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {partywiseVoting.reduce((sum, r) => sum + (r.bsp || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {partywiseVoting.reduce((sum, r) => sum + (r.ss_ubt || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {partywiseVoting.reduce((sum, r) => sum + (r.mns || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {partywiseVoting.reduce((sum, r) => sum + (r.independent || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-red-500">
                      {partywiseVoting.reduce((sum, r) => sum + (r.nota || 0), 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {partywiseVoting.reduce((sum, r) => sum + (r.total_votes || 0), 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {t(`Showing ${partywiseVoting.length} voting records`, `${partywiseVoting.length} मतदान रेकॉर्ड दाखवत आहे`)}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Building className="h-5 w-5" />
                {t('Nagpur Municipal Corporation', 'नागपूर महानगरपालिका')}
              </h3>
              <p className="text-blue-200 text-sm">
                {t('Civil Lines, Nagpur - 440001, Maharashtra, India', 
                   'सिव्हिल लाईन्स, नागपूर - 440001, महाराष्ट्र, भारत')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">{t('Contact', 'संपर्क')}</h3>
              <div className="space-y-2 text-blue-200 text-sm">
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4" /> 0712-2567001
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4" /> info@nmcnagpur.gov.in
                </p>
                <p className="flex items-center gap-2">
                  <Globe className="h-4 w-4" /> www.nmcnagpur.gov.in
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">{t('Quick Links', 'द्रुत दुवे')}</h3>
              <div className="space-y-2 text-blue-200 text-sm">
                <p className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <ExternalLink className="h-4 w-4" /> {t('Official Website', 'अधिकृत वेबसाइट')}
                </p>
                <p className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <ExternalLink className="h-4 w-4" /> {t('State Election Commission', 'राज्य निवडणूक आयोग')}
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-blue-800 mt-8 pt-4 text-center text-blue-200 text-sm">
            © 2025-26 {t('Nagpur Municipal Corporation. All Rights Reserved.', 'नागपूर महानगरपालिका. सर्व हक्क राखीव.')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CitizenPortal;
